import { format } from "date-fns";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useContext, useEffect, useState } from "react";
import { ToastContext } from "../../../../App";
import { useGetStationStatisticQuery } from "../../../../redux/services/statistic";
import {
  centerTextPlugin,
  drawCircles,
  formatDates,
  generateRawTimeArray,
  generateTimeArray,
  parseStationData,
} from "../../../../utils/charts";
import getSubdomain from "../../../../utils/getSubdomain";
import * as styles from "./StationStatistics.module.css";

const StationStatistics = ({
  selectedStation,
  selectedDates,
  selectedSlot,
  slotsData,
  stationName,
}) => {
  const showToast = useContext(ToastContext);

  const {
    data: stationStatistic,
    isSuccess: isStationStatisticSuccess,
    isError: isStationStatisticError,
    error: stationStatisticError,
  } = useGetStationStatisticQuery({
    subdomain: getSubdomain(),
    dates: formatDates(selectedDates),
    slot: selectedSlot,
    stationId: selectedStation,
  });

  const [doughnutData, setDoughnutData] = useState({});
  const [doughnutOptions, setDoughnutOptions] = useState({});
  const [rawDoughnutData, setDoughnutRawData] = useState([]);

  const [barData, setBarData] = useState({});
  const [barOptions, setBarOptions] = useState({});
  const [labelsCount, setLabelsCount] = useState(0);

  const [shortest, setShortest] = useState("00:00:00");
  const [longest, setLongest] = useState("00:00:00");
  const [average, setAverage] = useState("00:00:00");
  const [trimmedAverage, setTrimmedAverage] = useState("00:00:00");

  const [avgCost, setAvgCost] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [menuOptions, setMenuOptions] = useState([]);

  useEffect(() => {
    if (isStationStatisticError)
      showToast(
        "error",
        stationStatisticError?.status,
        stationStatisticError?.data?.message
      );
  }, [isStationStatisticError]);

  useEffect(() => {
    if (isStationStatisticSuccess) {
      const now = format(new Date(), "yyyy-MM-dd");
      let labels = [];
      let rawLabels = [];

      let startDate = "";
      let endDate = "";

      if (selectedSlot === "all-slots") {
        startDate = new Date(`${now} 00:00:00`);
        endDate = new Date(`${now} 23:45:00`);
      } else {
        const slot = slotsData.find((sl) => sl.slot_id === selectedSlot);
        if (!slot) {
          showToast(
            "warn",
            "Station",
            "The stations doesn't have a slot for this date"
          );
          return;
        }
        startDate = new Date(`${now} ${slot.start}`);
        endDate = new Date(`${now} ${slot.end}`);
      }

      const intervalMinutes = 15;

      labels = generateTimeArray(startDate, endDate, intervalMinutes);
      rawLabels = generateRawTimeArray(startDate, endDate, intervalMinutes);

      setLabelsCount(labels.length);

      const {
        data,
        options,
        parsedData,
        avgCost,
        totalCost,
        shortest,
        longest,
        average,
        trimmed_average,
        barData,
        barOptions,
        menu,
      } = parseStationData(stationStatistic, labels, rawLabels);

      setDoughnutRawData(parsedData);
      setDoughnutData(data);
      setDoughnutOptions(options);
      setShortest(shortest);
      setLongest(longest);
      setAverage(average);
      setTrimmedAverage(trimmed_average);
      setBarData(barData);
      setBarOptions(barOptions);
      setAvgCost(avgCost);
      setTotalCost(totalCost);
      setMenuOptions(menu);
    }
  }, [isStationStatisticSuccess, stationStatistic]);

  const chartWidth = `${labelsCount * 70}px`;

  const formatCurrency = (value) => {
    return value.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  const visibleTemplate = (value) => {
    if (value.visible) return "Yes";
    else return "No";
  };

  return (
    <>
      <div className={styles.chartsBlock}>
        <h3>Overview</h3>
        <div className={`${styles.cardWrapper}`}>
          <Card title="Total Orders" className={styles.doughnutCard}>
            <div className={styles.totalOrdersCardContent}>
              <Chart
                type="doughnut"
                data={doughnutData}
                options={doughnutOptions}
                plugins={[centerTextPlugin, drawCircles]}
                className={styles.doughnut}
              />
              <div className={styles.customLegend}>
                {rawDoughnutData.map((item, index) => (
                  <div key={index} className={styles.label}>
                    <span
                      className={styles.labelDot}
                      style={{ backgroundColor: item.color }}
                    />
                    <div className="flex flex-column">
                      <span className={styles.labelTitle}>{item.label}</span>
                      <span className={styles.labelValue}>{item.value}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
          <Card title="Orders per 15 mins" className={styles.barCard}>
            <div className={styles.barCardContent}>
              <div className={styles.barChartWrapper}>
                <div
                  className={styles.barChartContent}
                  style={{ width: chartWidth }}
                >
                  <Chart
                    type="bar"
                    data={barData}
                    options={barOptions}
                    className={styles.barChart}
                    plugins={[ChartDataLabels]}
                  />
                </div>
              </div>
              <div className={`${styles.barLegend}`}>
                <div>
                  <span />
                  <p>Highest for TimeSlot</p>
                </div>
                <div>
                  <span />
                  <p>2nd highest</p>
                </div>
                <div>
                  <span />
                  <p>3rd highest</p>
                </div>
              </div>
            </div>
          </Card>
          <Card title="Completion Time" className={styles.timeCard}>
            <div className={styles.timeBlocksWrapper}>
              <div className={styles.timeBlock}>
                <span>Shortest</span>
                <p>{shortest}</p>
              </div>
              <div className={styles.timeBlock}>
                <span>Longest</span>
                <p>{longest}</p>
              </div>
              <div className={styles.timeBlock}>
                <span>Average</span>
                <p>{average}</p>
              </div>
              <div className={styles.timeBlock}>
                <span>Trimmed Avg</span>
                <p>{trimmedAverage}</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <div className={styles.statsBlock}>
        <div className="flex justify-content-between">
          <h3>{stationName}</h3>
          {/* <Button
            size="small"
            icon="pi pi-download"
            label="EXPORT TO CSV"
            text
            severity="info"
          /> */}
        </div>
        <div className={styles.cost}>
          <div className={styles.costCardWrapper}>
            <div className={`${styles.costCard} shadow-2 border-round-2xl`}>
              <span>Average Cost Per Order</span>
              <p>$ {avgCost}</p>
            </div>
            <div className={`${styles.costCard} shadow-2 border-round-2xl`}>
              <span>Station Total Cost</span>
              <p>$ {totalCost}</p>
            </div>
          </div>

          <Card className={styles.menuCard} title="Menu Items Count">
            {menuOptions.length > 0 &&
              menuOptions.map((item, index) => (
                <div key={index}>
                  <h4>{item.group.group_name.toUpperCase()}</h4>
                  <DataTable
                    value={item.items}
                    showGridlines
                    className={styles.table}
                  >
                    <Column
                      field="name"
                      header="Menu Item"
                      body={(rowData) => {
                        return <div>{rowData.name}</div>;
                      }}
                    />
                    <Column
                      field="visible"
                      header="Was Visible?"
                      body={(rowData) => {
                        return <div>{visibleTemplate(rowData)}</div>;
                      }}
                    ></Column>
                    <Column field="orders" header="Orders" />
                    <Column
                      field="cost"
                      header="Cost"
                      body={(rowData) => {
                        return <div>{formatCurrency(rowData.cost)}</div>;
                      }}
                    ></Column>
                    <Column
                      field="total_cost"
                      header="Total Cost"
                      body={(rowData) => {
                        return <div>{formatCurrency(rowData.total_cost)}</div>;
                      }}
                    ></Column>
                  </DataTable>
                </div>
              ))}
          </Card>
        </div>
      </div>
    </>
  );
};

export default StationStatistics;
